import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Award, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function csv(arr) { return (arr || []).join(", "); }
function fromCsv(s) { return s.split(",").map((x) => x.trim()).filter(Boolean); }

export default function ProfilePage() {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState(null);
    const [certs, setCerts] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user) {
            const p = user.profile || {};
            setForm({
                name: user.name || "", photo: p.photo || "", college: p.college || "",
                degree: p.degree || "", specialization: p.specialization || "", year: p.year || "",
                career_goal: p.career_goal || "", github: p.github || "", linkedin: p.linkedin || "",
                resume_url: p.resume_url || "", skills: csv(p.skills), interests: csv(p.interests),
                achievements: csv(p.achievements), projects: p.projects || [], certifications: p.certifications || [],
            });
        }
    }, [user]);

    useEffect(() => {
        if (user?.role === "student") api.get("/student/certificates").then((r) => setCerts(r.data)).catch(() => {});
    }, [user]);

    if (!form) return <DashboardLayout title="Profile"><p className="text-sm text-muted-foreground">Loading...</p></DashboardLayout>;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/profile", {
                ...form,
                skills: fromCsv(form.skills), interests: fromCsv(form.interests), achievements: fromCsv(form.achievements),
            });
            const { data } = await api.get("/auth/me");
            setUser(data);
            toast.success("Profile saved!");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const updateListItem = (key, idx, field, value) => {
        const list = [...form[key]];
        list[idx] = { ...list[idx], [field]: value };
        set(key, list);
    };

    const FIELDS = [
        ["name", "Full name"], ["photo", "Profile photo URL"], ["college", "College / University"],
        ["degree", "Degree (e.g. B.Tech IT)"], ["specialization", "Specialization"], ["year", "Current year"],
        ["career_goal", "Career goal"], ["github", "GitHub URL"], ["linkedin", "LinkedIn URL"], ["resume_url", "Resume URL"],
    ];

    return (
        <DashboardLayout title="Your TechBloom Profile">
            <div className="mx-auto max-w-4xl space-y-8" data-testid="profile-page">
                <div className="card-soft p-8">
                    <h1 className="font-display text-2xl font-bold tracking-tight">Everything you've built. One career profile.</h1>
                    <p className="mt-1 text-sm text-muted-foreground">This profile is attached automatically to every application you make.</p>
                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                        {FIELDS.map(([key, label]) => (
                            <div key={key} className="space-y-2">
                                <Label htmlFor={key}>{label}</Label>
                                <Input id={key} data-testid={`profile-${key.replace(/_/g, "-")}-input`} value={form[key]} onChange={(e) => set(key, e.target.value)} className="rounded-lg" />
                            </div>
                        ))}
                        <div className="space-y-2">
                            <Label htmlFor="skills">Skills (comma separated)</Label>
                            <Input id="skills" data-testid="profile-skills-input" value={form.skills} onChange={(e) => set("skills", e.target.value)} className="rounded-lg" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="interests">Interests (comma separated)</Label>
                            <Input id="interests" data-testid="profile-interests-input" value={form.interests} onChange={(e) => set("interests", e.target.value)} className="rounded-lg" />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="achievements">Achievements (comma separated)</Label>
                            <Input id="achievements" data-testid="profile-achievements-input" value={form.achievements} onChange={(e) => set("achievements", e.target.value)} className="rounded-lg" />
                        </div>
                    </div>
                </div>

                <div className="card-soft p-8">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-lg font-semibold">Projects</h2>
                        <Button data-testid="add-project-button" size="sm" variant="outline" className="rounded-full" onClick={() => set("projects", [...form.projects, { title: "", description: "" }])}>
                            <Plus className="mr-1 h-4 w-4" /> Add
                        </Button>
                    </div>
                    <div className="mt-4 space-y-4">
                        {form.projects.map((p, i) => (
                            <div key={i} className="flex items-start gap-3 rounded-xl border border-border/40 p-4" data-testid={`profile-project-${i}`}>
                                <div className="flex-1 space-y-3">
                                    <Input placeholder="Project title" value={p.title} onChange={(e) => updateListItem("projects", i, "title", e.target.value)} className="rounded-lg" data-testid={`project-title-${i}`} />
                                    <Input placeholder="Short description" value={p.description} onChange={(e) => updateListItem("projects", i, "description", e.target.value)} className="rounded-lg" data-testid={`project-desc-${i}`} />
                                </div>
                                <Button size="icon" variant="ghost" data-testid={`remove-project-${i}`} onClick={() => set("projects", form.projects.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-accent" /></Button>
                            </div>
                        ))}
                        {form.projects.length === 0 && <p className="text-sm text-muted-foreground">No projects added yet.</p>}
                    </div>
                </div>

                <div className="card-soft p-8">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-lg font-semibold">Certifications</h2>
                        <Button data-testid="add-cert-button" size="sm" variant="outline" className="rounded-full" onClick={() => set("certifications", [...form.certifications, { title: "", issuer: "" }])}>
                            <Plus className="mr-1 h-4 w-4" /> Add
                        </Button>
                    </div>
                    <div className="mt-4 space-y-4">
                        {form.certifications.map((c, i) => (
                            <div key={i} className="flex items-start gap-3 rounded-xl border border-border/40 p-4" data-testid={`profile-cert-${i}`}>
                                <div className="flex-1 space-y-3">
                                    <Input placeholder="Certification title" value={c.title} onChange={(e) => updateListItem("certifications", i, "title", e.target.value)} className="rounded-lg" data-testid={`cert-title-${i}`} />
                                    <Input placeholder="Issuer (e.g. Coursera)" value={c.issuer} onChange={(e) => updateListItem("certifications", i, "issuer", e.target.value)} className="rounded-lg" data-testid={`cert-issuer-${i}`} />
                                </div>
                                <Button size="icon" variant="ghost" data-testid={`remove-cert-${i}`} onClick={() => set("certifications", form.certifications.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-accent" /></Button>
                            </div>
                        ))}
                        {form.certifications.length === 0 && <p className="text-sm text-muted-foreground">No certifications added yet.</p>}
                    </div>
                </div>

                <Button data-testid="save-profile-button" disabled={busy} className="rounded-full px-8" onClick={save}>
                    <Save className="mr-2 h-4 w-4" />{busy ? "Saving..." : "Save Profile"}
                </Button>

                {certs && (
                    <div className="card-soft p-8" data-testid="certificates-section">
                        <h2 className="font-display text-lg font-semibold">Earned Certificates</h2>
                        {certs.internship_certificates.length === 0 ? (
                            <p className="mt-3 text-sm text-muted-foreground">Complete an internship program to earn your TechBloom certificate.</p>
                        ) : (
                            <div className="mt-4 space-y-3">
                                {certs.internship_certificates.map((c) => (
                                    <div key={c.id} className="flex items-center gap-4 rounded-xl bg-primary/5 p-4" data-testid={`certificate-${c.id}`}>
                                        <span className="grid h-10 w-10 place-items-center rounded-full bg-secondary/30"><Award className="h-5 w-5" /></span>
                                        <div>
                                            <p className="text-sm font-semibold">{c.title}</p>
                                            <p className="font-mono text-xs text-muted-foreground">{c.id} · {new Date(c.issued_at).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
